import React from "react";
import { Link } from "react-router-dom";
import { PRICING } from "../lib/data";
import { ArrowUpRight, Sparkles } from "lucide-react";

const money = (v) => {
  if (typeof v === "string") return v;
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
};

function BookButton({ service, label = "Book now" }) {
  const href = `/consultation?service=${encodeURIComponent(service)}`;
  return (
    <Link to={href} data-testid={`book-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="inline-flex items-center gap-1 text-xs font-medium text-[#9A7420] hover:text-[#2B2340] transition-colors whitespace-nowrap">
      {label} <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
    </Link>
  );
}

function PriceTable({ block }) {
  if (block.free) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4 p-6 bg-[#F8F5EE] border border-[#E4CE8F]/60 rounded-xl">
        <p className="text-sm text-[#57515F]">Book directly with our team.</p>
        <p className="font-display text-2xl text-[#2B2340]">Free</p>
        <BookButton service={block.name} label="Book consultation" />
      </div>
    );
  }
  if (block.contact) {
    return (
      <div className="p-6 bg-white border border-[#DAD5E2] rounded-xl">
        {block.lines && (
          <ul className="text-sm text-[#57515F] space-y-1 mb-4">
            {block.lines.map((l) => <li key={l}>• {l}</li>)}
          </ul>
        )}
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#8B8494] italic">Pricing varies by area / plan</p>
          <BookButton service={block.name} label="Get a quote" />
        </div>
      </div>
    );
  }

  const cols = block.columns;
  const hasDuration = block.rows.some((r) => r.duration);

  return (
    <div className="overflow-x-auto border border-[#DAD5E2] rounded-2xl bg-white">
      <table className="w-full text-sm">
        <thead className="bg-[#F5F2FA] text-left text-[#57515F]">
          <tr>
            <th className="px-5 py-4 font-medium">Area / mode</th>
            {cols.map((c, i) => {
              const isLast = i === cols.length - 1;
              const showBadge = isLast && cols.length > 1;
              return (
                <th key={c} className="px-5 py-4 font-medium whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    {showBadge && (
                      <span data-testid={`best-value-${block.key}`} className="inline-flex items-center gap-1 text-[9px] font-semibold tracking-wider uppercase text-[#1A1A1A] bg-gradient-to-r from-[#DCC17C] to-[#C6A15B] rounded-full px-2 py-0.5 shadow-sm">
                        <Sparkles className="w-2.5 h-2.5" strokeWidth={2.5} /> Best value
                      </span>
                    )}
                    <span>{c}</span>
                  </div>
                </th>
              );
            })}
            {hasDuration && <th className="px-5 py-4 font-medium text-right">Duration</th>}
            <th className="px-5 py-4 font-medium text-right"> </th>
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, i) => (
            <tr key={i} className="border-t border-[#DAD5E2]">
              <td className="px-5 py-4 text-[#2B2340] max-w-md">{row.area}</td>
              {row.prices.map((p, idx) => {
                const isLast = idx === row.prices.length - 1;
                const isBestValue = isLast && row.prices.length > 1;
                return (
                  <td key={idx} className={`px-5 py-4 text-right whitespace-nowrap ${isBestValue ? "bg-[#F8F5EE]/60" : (isLast ? "" : "text-[#8B8494]")}`}>
                    {isLast ? (
                      <>
                        <span className={`font-display text-lg ${isBestValue ? "text-[#7a5c12]" : "text-[#2B2340]"}`}>{money(p)}</span>
                        {row.savings && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-[#9A7420] bg-white border border-[#E4CE8F]/50 rounded-full px-2 py-0.5">
                            <Sparkles className="w-3 h-3" strokeWidth={2} /> save {money(row.savings)}
                          </span>
                        )}
                      </>
                    ) : money(p)}
                  </td>
                );
              })}
              {hasDuration && <td className="px-5 py-4 text-right text-[#8B8494] whitespace-nowrap">{row.duration || ""}</td>}
              <td className="px-5 py-4 text-right">
                <BookButton service={block.name} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Pricing() {
  return (
    <main data-testid="pricing-page" className="pt-28 pb-28 min-h-screen relative z-10">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="overline text-[#9A7420] mb-4">Investment</p>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-6">Treatment Pricing</h1>
          <p className="text-[#57515F] max-w-2xl mx-auto text-base">
            Transparent, à la carte pricing. Every treatment includes a personal consultation and after-care.
          </p>
          <div data-testid="pricing-policy" className="mt-8 inline-flex flex-wrap items-center gap-3 justify-center">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-[#1A1A1A] bg-gradient-to-r from-[#DCC17C] to-[#C6A15B] rounded-full px-4 py-2 shadow-sm">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> 3-session package · 20% OFF
            </span>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-[#1A1A1A] bg-gradient-to-r from-[#DCC17C] to-[#C6A15B] rounded-full px-4 py-2 shadow-sm">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> 5-session package · 30% OFF
            </span>
          </div>
        </div>

        {/* Payment methods pill row */}
        <div data-testid="payment-methods" className="flex flex-wrap items-center justify-center gap-3 mb-16">
          <span className="text-xs text-[#8B8494] uppercase tracking-wider mr-2">Ways to pay</span>
          {["Visa", "Mastercard", "Amex", "Apple Pay", "Google Pay", "Cash App", "Klarna", "Afterpay"].map((m) => (
            <span key={m} className="text-xs font-medium px-3 py-1.5 bg-white border border-[#DAD5E2] rounded-full text-[#2B2340]">
              {m}
            </span>
          ))}
          <span className="text-xs text-[#8B8494] italic w-full text-center mt-2">
            Split any purchase into 4 interest-free payments with Klarna or Afterpay at checkout.
          </span>
        </div>

        {/* Pricing blocks */}
        <div className="space-y-14">
          {PRICING.map((block) => (
            <section key={block.key} data-testid={`price-block-${block.key}`}>
              <div className="mb-5">
                <h2 className="font-display text-3xl md:text-4xl text-[#2B2340] mb-1">{block.name}</h2>
                {block.subtitle && <p className="text-sm text-[#57515F]">{block.subtitle}</p>}
              </div>
              <PriceTable block={block} />
            </section>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-24 text-center border-t border-[#DAD5E2] pt-12">
          <p className="overline text-[#9A7420] mb-3">Have questions</p>
          <h3 className="font-display text-3xl md:text-4xl text-[#2B2340] mb-4">Not sure which treatment fits you?</h3>
          <p className="text-[#57515F] mb-6 max-w-xl mx-auto text-sm">
            Book a complimentary 30-minute consultation and we&apos;ll build a personalized plan for your skin,
            body, and budget.
          </p>
          <Link to="/consultation" data-testid="pricing-consult-cta" className="btn-primary inline-flex">
            Book free consultation
          </Link>
        </div>

        <p className="text-xs text-[#8B8494] text-center mt-16">
          All prices in USD. Package pricing reflects prepayment for the full course.
          Individual results vary. Consultation may adjust recommendations.
        </p>
      </div>
    </main>
  );
}
